/**
 * Terms of Service Page
 * Legal terms for using CarbonRoute
 */
import React from 'react';
import { Link } from 'react-router-dom';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-600 to-emerald-600 py-12">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/login" className="text-sky-200 hover:text-white text-sm mb-4 inline-flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Login
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white mt-4">Terms of Service</h1>
          <p className="text-sky-100 mt-2">Last updated: December 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">

          {/* Introduction */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              Welcome to CarbonRoute, a supply chain emission calculator provided by Arantree Consulting Services
              ("we," "us," or "our"). By accessing or using our service, you agree to be bound by these Terms of Service.
              If you do not agree to these terms, please do not use our service.
            </p>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Service Description</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              CarbonRoute is a web-based tool that calculates carbon emissions for transportation and logistics operations.
              Our service provides:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Emission calculations for road, rail, sea, and air transport</li>
              <li>Route optimization suggestions</li>
              <li>Analytics and reporting features</li>
              <li>Data export capabilities</li>
            </ul>
          </section>

          {/* Account Registration */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. Account Registration</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              To use CarbonRoute, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
          </section>

          {/* Usage and Access */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">4. Usage and Access</h2>
            <p className="text-gray-600 leading-relaxed">
              Access to CarbonRoute and usage limits are determined at our discretion and may be modified.
              Continued use of the service may require a subscription or payment arrangement as determined
              by Arantree Consulting Services.
            </p>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Acceptable Use</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Use the service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Share your account credentials with others</li>
              <li>Use automated tools to access the service without permission</li>
              <li>Reproduce, distribute, or resell our service or data</li>
            </ul>
          </section>

          {/* Data and Calculations */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Data and Calculations</h2>
            <p className="text-gray-600 leading-relaxed">
              While we strive to provide accurate emission calculations, the results are estimates based on
              available emission factors and the data you provide. CarbonRoute should be used as a guide for
              sustainability planning. The emissions report generated must be verified by a GHG (Greenhouse Gas)
              certification provider for official reporting purposes. Without verification from a certified GHG
              auditor, the reports cannot be used for regulatory compliance or official sustainability reporting.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed">
              CarbonRoute and all associated content, features, and functionality are owned by Arantree Consulting
              Services and are protected by copyright, trademark, and other intellectual property laws. You may not
              copy, modify, or create derivative works without our express written permission.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              To the maximum extent permitted by law, Arantree Consulting Services shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages arising from your use of the service.
              Our total liability shall not exceed the amount you paid for the service in the twelve months prior
              to the claim.
            </p>
          </section>

          {/* Disclaimer */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-gray-600 leading-relaxed">
              The service is provided "as is" and "as available" without warranties of any kind, either express
              or implied. We do not warrant that the service will be uninterrupted, error-free, or completely secure.
            </p>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Modifications to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to modify these terms at any time. We will notify users of significant changes
              via email or through the service. Your continued use of CarbonRoute after such modifications
              constitutes acceptance of the updated terms.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">11. Termination</h2>
            <p className="text-gray-600 leading-relaxed">
              We may terminate or suspend your account at any time for violation of these terms or for any other
              reason at our discretion. Upon termination, your right to use the service will immediately cease.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These terms shall be governed by and construed in accordance with the laws of India. Any disputes
              arising from these terms shall be subject to the exclusive jurisdiction of the courts in India.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Arantree Consulting Services</p>
              <p className="text-gray-600">Email: admin@arantree.com</p>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <Link
            to="/privacy"
            className="text-sky-600 hover:text-sky-700 font-medium"
          >
            View Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Terms;
